import { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiGet, apiPost } from '../utils/api'

const toRecord = (value: unknown): Record<string, any> | null => (value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : null)
const toArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? value as T[] : [])
const formatList = (value: unknown): string | null => {
  if (Array.isArray(value)) {
    const normalized = value.filter((item) => item !== null && item !== undefined).map((item) => String(item))
    return normalized.length ? normalized.join(', ') : null
  }
  return typeof value === 'string' && value.trim() ? value : null
}
const displayValue = (value: unknown): string => {
  const asList = formatList(value)
  if (asList) return asList
  if (value === null || value === undefined) return 'N/A'
  if (typeof value === 'number') return value.toLocaleString()
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

type Job = {
  id: number
  tenant_id: number
  endpoint_id: number
  task_type: string
  state: string
  created_at: string
  started_at?: string
  finished_at?: string
  log: string
  payload?: Record<string, any> | null
  result?: Record<string, any> | null
  backup_mode?: string | null
  parent_job_id?: number | null
  parent_snapshot?: string | null
  snapshot_id?: string | null
  chain_id?: number | null
}

type BackupChain = {
  id: number
  endpoint_name?: string | null
  repository_name?: string | null
  target_name?: string | null
  policy_name?: string | null
  schedule_name?: string | null
  last_full_job_id?: number | null
  last_full_snapshot?: string | null
  last_snapshot_job_id?: number | null
  last_snapshot_id?: string | null
  last_snapshot_mode?: string | null
  non_full_count: number
  status: string
  created_at: string
  updated_at?: string | null
}

export default function JobDetail() {
  const { jobId } = useParams<{ jobId: string }>()
  const [job, setJob] = useState<Job | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [chain, setChain] = useState<BackupChain | null>(null)
  const [chainLoading, setChainLoading] = useState(false)
  const [chainError, setChainError] = useState<string | undefined>()
  const [isResetting, setIsResetting] = useState(false)

  useEffect(() => {
    if (!jobId) {
      setError('Missing job id')
      setLoading(false)
      return
    }
    let timer: any
    async function load() {
      setLoading(true)
      try {
        setError(undefined)
        const j = await apiGet(`/jobs/${jobId}`)
        setJob(j)
        if (j.state === 'queued' || j.state === 'running') {
          timer = setTimeout(load, 2000)
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load job')
      } finally {
        setLoading(false)
      }
    }
    load()
    return () => timer && clearTimeout(timer)
  }, [jobId])

  useEffect(() => {
    if (!job || !job.chain_id) {
      setChain(null)
      setChainError(undefined)
      setChainLoading(false)
      return
    }
    let cancelled = false
    async function loadChain() {
      setChainLoading(true)
      try {
        const data = await apiGet(`/tenants/${job.tenant_id}/backup-chains/${job.chain_id}`)
        if (!cancelled) {
          setChain(data)
          setChainError(undefined)
        }
      } catch (e: any) {
        if (!cancelled) {
          setChain(null)
          setChainError(e?.message || 'Unable to load backup chain')
        }
      } finally {
        if (!cancelled) setChainLoading(false)
      }
    }
    loadChain()
    return () => { cancelled = true }
  }, [job?.chain_id, job?.tenant_id])

  const backupModeLabel = useMemo(() => {
    if (!job) return ''
    const mode = (job.backup_mode || '').toLowerCase()
    if (!mode) return 'Full'
    return mode.charAt(0).toUpperCase() + mode.slice(1)
  }, [job])

  const handleResetChain = async () => {
    if (!job || !job.chain_id) return
    if (!confirm('Reset backup chain? The next backup will run as full.')) return
    setIsResetting(true)
    try {
      const updated = await apiPost(`/tenants/${job.tenant_id}/backup-chains/${job.chain_id}/reset`, { reset_full: true })
      setChain(updated)
      setChainError(undefined)
    } catch (e: any) {
      setChainError(e?.message || 'Reset failed')
    } finally {
      setIsResetting(false)
    }
  }

  if (loading && !job) return <p className="text-gray-500 dark:text-gray-400">Loading…</p>
  if (error) return <p className="text-red-600">{error}</p>
  if (!job) return <p>Not found</p>

  const resultMeta = toRecord(job.result)
  const workspaceSummary = toRecord(resultMeta?.workspace_summary)
  const workspaceState = toRecord(resultMeta?.workspace_state)
  const workspaceModules = toArray<string>(workspaceSummary?.modules)
  const workspaceModuleSummaries = toArray<Record<string, any>>(workspaceSummary?.modules_summary)
  const workspaceResolvedAt = typeof workspaceSummary?.resolved_at === 'string'
    ? new Date(workspaceSummary.resolved_at).toLocaleString()
    : null
  const workspaceUsersCount = (() => {
    if (typeof workspaceSummary?.users_processed === 'number') return workspaceSummary.users_processed
    if (typeof workspaceSummary?.users_total === 'number') return workspaceSummary.users_total
    if (Array.isArray(workspaceSummary?.users)) return workspaceSummary.users.length
    return null
  })()
  const workspaceSampleUsers = (() => {
    if (Array.isArray(workspaceSummary?.sample_users) && workspaceSummary.sample_users.length) {
      return workspaceSummary.sample_users.slice(0, 5).join(', ')
    }
    if (Array.isArray(workspaceSummary?.users) && workspaceSummary.users.length && workspaceSummary.users.length <= 5) {
      return workspaceSummary.users.join(', ')
    }
    if (workspaceSummary?.sample_user) return String(workspaceSummary.sample_user)
    return null
  })()
  const workspaceScopes = formatList(workspaceSummary?.scopes)
  const workspaceStateUpdated = typeof workspaceState?.updated_at === 'string'
    ? new Date(workspaceState.updated_at).toLocaleString()
    : null
  const workspaceStateModules = toRecord(workspaceState?.modules)
  const workspaceTrackedUsers = (() => {
    if (Array.isArray(workspaceState?.users)) return workspaceState.users.length
    if (workspaceState?.users && typeof workspaceState.users === 'object') return Object.keys(workspaceState.users).length
    return null
  })()

  const exchangeSummary = toRecord(resultMeta?.exchange_summary)
  const exchangeState = toRecord(resultMeta?.exchange_state)
  const exchangeModules = toArray<string>(exchangeSummary?.modules)
  const exchangeModuleSummaries = toArray<Record<string, any>>(exchangeSummary?.modules_summary)
  const mailboxCount = typeof exchangeSummary?.mailboxes_total === 'number'
    ? exchangeSummary.mailboxes_total
    : typeof exchangeSummary?.mailboxes === 'number'
      ? exchangeSummary.mailboxes
      : Array.isArray(exchangeSummary?.mailboxes)
        ? exchangeSummary.mailboxes.length
        : null
  const sampleMailboxes = (() => {
    if (Array.isArray(exchangeSummary?.mailboxes_sample)) {
      return exchangeSummary.mailboxes_sample.slice(0, 5).join(', ')
    }
    if (Array.isArray(exchangeSummary?.mailboxes) && exchangeSummary.mailboxes.length) {
      return exchangeSummary.mailboxes.slice(0, 5).join(', ')
    }
    return null
  })()
  const resolvedAt = typeof exchangeSummary?.resolved_at === 'string'
    ? new Date(exchangeSummary.resolved_at).toLocaleString()
    : null
  const exchangeScopes = formatList(exchangeSummary?.scopes)
  const exchangeStateUpdated = typeof exchangeState?.updated_at === 'string'
    ? new Date(exchangeState.updated_at).toLocaleString()
    : null
  const exchangeStateModules = toRecord(exchangeState?.modules)

  const onedriveSummary = toRecord(resultMeta?.onedrive_summary)
  const onedriveState = toRecord(resultMeta?.onedrive_state)
  const onedriveResolvedAt = typeof onedriveSummary?.resolved_at === 'string'
    ? new Date(onedriveSummary.resolved_at).toLocaleString()
    : null
  const onedriveUsersCount = (() => {
    if (typeof onedriveSummary?.users_processed === 'number') return onedriveSummary.users_processed
    if (typeof onedriveSummary?.users_total === 'number') return onedriveSummary.users_total
    if (Array.isArray(onedriveSummary?.users)) return onedriveSummary.users.length
    return null
  })()
  const onedriveSampleUsers = (() => {
    if (Array.isArray(onedriveSummary?.sample_users) && onedriveSummary.sample_users.length) {
      return onedriveSummary.sample_users.slice(0, 5).join(', ')
    }
    if (Array.isArray(onedriveSummary?.users) && onedriveSummary.users.length && onedriveSummary.users.length <= 5) {
      return onedriveSummary.users.join(', ')
    }
    if (onedriveSummary?.sample_user) return String(onedriveSummary.sample_user)
    return null
  })()
  const onedriveFilesCount = (() => {
    if (typeof onedriveSummary?.files_total === 'number') return onedriveSummary.files_total
    if (typeof onedriveSummary?.files_exported === 'number') return onedriveSummary.files_exported
    if (typeof onedriveSummary?.files_downloaded === 'number') return onedriveSummary.files_downloaded
    return null
  })()
  const onedriveBytesCount = (() => {
    if (typeof onedriveSummary?.bytes_total === 'number') return onedriveSummary.bytes_total
    if (typeof onedriveSummary?.bytes_exported === 'number') return onedriveSummary.bytes_exported
    if (typeof onedriveSummary?.bytes_downloaded === 'number') return onedriveSummary.bytes_downloaded
    return null
  })()
  const onedriveIncludeShared = typeof onedriveSummary?.include_shared === 'boolean' ? onedriveSummary.include_shared : undefined
  const onedriveQuery = typeof onedriveSummary?.drive_query === 'string' ? onedriveSummary.drive_query : undefined
  const onedriveExcludeExtensions = (() => {
    if (Array.isArray(onedriveSummary?.exclude_extensions)) return onedriveSummary.exclude_extensions
    if (typeof onedriveSummary?.exclude_extensions === 'string') {
      return onedriveSummary.exclude_extensions.split(/[\n,\s]+/).map((value: string) => value.trim()).filter(Boolean)
    }
    return [] as string[]
  })()
  const onedriveMaxFiles = typeof onedriveSummary?.max_files === 'number' ? onedriveSummary.max_files : undefined
  const onedriveMaxBytes = typeof onedriveSummary?.max_bytes === 'number' ? onedriveSummary.max_bytes : undefined
  const onedriveStateUpdated = typeof onedriveState?.updated_at === 'string'
    ? new Date(onedriveState.updated_at).toLocaleString()
    : null
  const onedriveTrackedUsers = (() => {
    if (Array.isArray(onedriveState?.users)) return onedriveState.users.length
    if (onedriveState?.users && typeof onedriveState.users === 'object') return Object.keys(onedriveState.users).length
    return null
  })()
  const onedriveTrackedDrives = (() => {
    if (Array.isArray(onedriveState?.drives)) return onedriveState.drives.length
    if (onedriveState?.drives && typeof onedriveState.drives === 'object') return Object.keys(onedriveState.drives).length
    return null
  })()

  return (
    <div className="card">
      <h2 className="font-semibold mb-3">Job #{job.id}</h2>
      <p>Type: {job.task_type}</p>
      <p>Endpoint: {job.endpoint_id}</p>
      <p>
        State:{' '}
        <span
          className={(() => {
            const base = 'ml-1 px-2 py-0.5 rounded text-xs font-medium'
            switch (job.state) {
              case 'success':
                return base + ' bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
              case 'failed':
                return base + ' bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
              case 'running':
                return base + ' bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
              case 'queued':
                return base + ' bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
              default:
                return base + ' bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
            }
          })()}
        >
          {job.state}
        </span>
      </p>
      <p>Created: {new Date(job.created_at).toLocaleString()}</p>
      {job.started_at && <p>Started: {new Date(job.started_at).toLocaleString()}</p>}
      {job.finished_at && <p>Finished: {new Date(job.finished_at).toLocaleString()}</p>}

      <div className="mt-3 grid gap-2 md:grid-cols-2 text-sm">
        <div>
          <h3 className="font-semibold text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Backup</h3>
          <ul className="mt-1 space-y-1">
            <li>Mode: <span className="font-medium">{backupModeLabel}</span></li>
            <li>Snapshot: <code>{job.snapshot_id || '(none)'}</code></li>
            {job.parent_snapshot && <li>Parent snapshot: <code>{job.parent_snapshot}</code></li>}
            {job.parent_job_id && (
              <li>
                Parent job:{' '}
                <Link className="link" to={`/jobs/${job.parent_job_id}`}>
                  #{job.parent_job_id}
                </Link>
              </li>
            )}
          </ul>
        </div>
        <div>
          <h3 className="font-semibold text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Metadata</h3>
          <ul className="mt-1 space-y-1">
            <li>Tenant: {job.tenant_id}</li>
            <li>Chain: {job.chain_id ? `#${job.chain_id}` : '—'}</li>
            <li>Payload note: {job.payload?.note || '—'}</li>
          </ul>
        </div>
      </div>

      {(workspaceSummary || workspaceState) && (
        <div className="mt-4 border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/10 rounded-lg p-3 text-xs">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-sm">Google Workspace</h3>
            {workspaceResolvedAt && <span className="text-[11px] text-amber-700 dark:text-amber-200">Resolved at {workspaceResolvedAt}</span>}
          </div>
          {workspaceSummary && (
            <ul className="mt-2 space-y-1">
              <li>Mode: <span className="font-medium">{displayValue(workspaceSummary.mode)}</span></li>
              <li>Modules: <span className="font-medium">{workspaceModules.length ? workspaceModules.join(', ') : 'N/A'}</span></li>
              <li>Users: <span className="font-medium">{workspaceUsersCount !== null ? workspaceUsersCount.toLocaleString() : 'N/A'}</span></li>
              {workspaceSampleUsers && <li>Sample: <span className="font-medium">{workspaceSampleUsers}</span></li>}
              {workspaceSummary.primary_user && <li>Primary user: <span className="font-medium">{displayValue(workspaceSummary.primary_user)}</span></li>}
              {workspaceSummary.admin_user && <li>Admin user: <span className="font-medium">{displayValue(workspaceSummary.admin_user)}</span></li>}
              {workspaceSummary.group_email && <li>Group: <span className="font-medium">{displayValue(workspaceSummary.group_email)}</span></li>}
              {workspaceScopes && <li>Scopes: <span className="font-medium">{workspaceScopes}</span></li>}
            </ul>
          )}
          {workspaceModuleSummaries.length > 0 && (
            <div className="mt-3">
              <h4 className="font-semibold text-[11px] uppercase tracking-wide text-amber-800 dark:text-amber-200">Module summary</h4>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                {workspaceModuleSummaries.map((moduleSummary, index) => {
                  const moduleKey = String(moduleSummary.module || index)
                  const processedUsers = typeof moduleSummary.users_processed === 'number'
                    ? moduleSummary.users_processed
                    : typeof moduleSummary.accounts_processed === 'number'
                      ? moduleSummary.accounts_processed
                      : undefined
                  const files = typeof moduleSummary.files_exported === 'number'
                    ? moduleSummary.files_exported
                    : typeof moduleSummary.files_downloaded === 'number'
                      ? moduleSummary.files_downloaded
                      : undefined
                  const messages = typeof moduleSummary.messages_downloaded === 'number'
                    ? moduleSummary.messages_downloaded
                    : undefined
                  const events = typeof moduleSummary.events_downloaded === 'number'
                    ? moduleSummary.events_downloaded
                    : undefined
                  const bytes = typeof moduleSummary.bytes_downloaded === 'number' ? moduleSummary.bytes_downloaded : undefined
                  const errors = Array.isArray(moduleSummary.errors) ? moduleSummary.errors.length : (typeof moduleSummary.errors === 'number' ? moduleSummary.errors : 0)
                  return (
                    <div key={`${moduleKey}-${index}`} className="rounded border border-amber-200 dark:border-amber-900/40 bg-white/70 dark:bg-amber-950/30 p-2">
                      <p className="font-semibold capitalize text-sm">{displayValue(moduleSummary.module)}</p>
                      <ul className="mt-1 space-y-1">
                        {processedUsers !== undefined && <li>Users processed: {processedUsers.toLocaleString()}</li>}
                        {files !== undefined && <li>Files exported: {files.toLocaleString()}</li>}
                        {messages !== undefined && <li>Messages downloaded: {messages.toLocaleString()}</li>}
                        {events !== undefined && <li>Events downloaded: {events.toLocaleString()}</li>}
                        {bytes !== undefined && <li>Bytes downloaded: {bytes.toLocaleString()}</li>}
                        {errors > 0 && <li className="text-red-600">Errors captured: {errors}</li>}
                        {moduleSummary.directory && <li>Directory: <code>{moduleSummary.directory}</code></li>}
                      </ul>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          {workspaceState && (
            <div className="mt-3">
              <h4 className="font-semibold text-[11px] uppercase tracking-wide text-amber-800 dark:text-amber-200">State</h4>
              <ul className="mt-1 space-y-1">
                <li>Updated at: {workspaceStateUpdated || displayValue(workspaceState.updated_at)}</li>
                <li>Tracked users: {workspaceTrackedUsers !== null ? workspaceTrackedUsers.toLocaleString() : 'N/A'}</li>
                {workspaceStateModules && <li>Modules tracked: {Object.keys(workspaceStateModules).join(', ') || 'N/A'}</li>}
              </ul>
              <details className="mt-2">
                <summary className="cursor-pointer text-[11px] font-semibold">View raw state</summary>
                <pre className="bg-amber-100 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/40 p-2 rounded text-[11px] overflow-auto max-h-64 whitespace-pre-wrap">
                  {JSON.stringify(workspaceState, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      )}

      {(exchangeSummary || exchangeState) && (
        <div className="mt-4 border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/10 rounded-lg p-3 text-xs">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-sm">Microsoft 365 Exchange</h3>
            {resolvedAt && <span className="text-[11px] text-blue-700 dark:text-blue-200">Resolved at {resolvedAt}</span>}
          </div>
          {exchangeSummary && (
            <ul className="mt-2 space-y-1">
              <li>Mode: <span className="font-medium">{displayValue(exchangeSummary.mode)}</span></li>
              <li>Modules: <span className="font-medium">{exchangeModules.length ? exchangeModules.join(', ') : 'N/A'}</span></li>
              <li>Mailboxes: <span className="font-medium">{mailboxCount !== null ? mailboxCount.toLocaleString() : 'N/A'}</span></li>
              {sampleMailboxes && <li>Sample: <span className="font-medium">{sampleMailboxes}</span></li>}
              {exchangeSummary.primary_user && <li>Primary user: <span className="font-medium">{displayValue(exchangeSummary.primary_user)}</span></li>}
              {exchangeSummary.admin_user && <li>Admin user: <span className="font-medium">{displayValue(exchangeSummary.admin_user)}</span></li>}
              {exchangeSummary.group_email && <li>Group: <span className="font-medium">{displayValue(exchangeSummary.group_email)}</span></li>}
              {exchangeScopes && <li>Scopes: <span className="font-medium">{exchangeScopes}</span></li>}
            </ul>
          )}
          {exchangeModuleSummaries.length > 0 && (
            <div className="mt-3">
              <h4 className="font-semibold text-[11px] uppercase tracking-wide text-blue-800 dark:text-blue-200">Module summary</h4>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                {exchangeModuleSummaries.map((moduleSummary, index) => {
                  const moduleKey = String(moduleSummary.module || index)
                  const processed = typeof moduleSummary.mailboxes_processed === 'number'
                    ? moduleSummary.mailboxes_processed
                    : Array.isArray(moduleSummary.mailboxes)
                      ? moduleSummary.mailboxes.length
                      : undefined
                  const messageCount = typeof moduleSummary.messages_downloaded === 'number'
                    ? moduleSummary.messages_downloaded
                    : typeof moduleSummary.events_downloaded === 'number'
                      ? moduleSummary.events_downloaded
                      : undefined
                  const bytes = typeof moduleSummary.bytes_downloaded === 'number' ? moduleSummary.bytes_downloaded : undefined
                  const errors = Array.isArray(moduleSummary.errors) ? moduleSummary.errors.length : (typeof moduleSummary.errors === 'number' ? moduleSummary.errors : 0)
                  return (
                    <div key={`${moduleKey}-${index}`} className="rounded border border-blue-200 dark:border-blue-900/40 bg-white/60 dark:bg-blue-950/30 p-2">
                      <p className="font-semibold capitalize text-sm">{displayValue(moduleSummary.module)}</p>
                      <ul className="mt-1 space-y-1">
                        {processed !== undefined && <li>Mailboxes processed: {processed}</li>}
                        {messageCount !== undefined && (
                          <li>
                            {moduleSummary.module === 'calendar' ? 'Events downloaded' : 'Messages downloaded'}: {messageCount.toLocaleString()}
                          </li>
                        )}
                        {bytes !== undefined && <li>Bytes downloaded: {bytes.toLocaleString()}</li>}
                        {errors > 0 && <li className="text-red-600">Errors captured: {errors}</li>}
                        {moduleSummary.directory && <li>Directory: <code>{moduleSummary.directory}</code></li>}
                      </ul>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          {exchangeState && (
            <div className="mt-3">
              <h4 className="font-semibold text-[11px] uppercase tracking-wide text-blue-800 dark:text-blue-200">State</h4>
              <ul className="mt-1 space-y-1">
                <li>Updated at: {exchangeStateUpdated || displayValue(exchangeState.updated_at)}</li>
                <li>Tracked mailboxes: {Array.isArray(exchangeState.mailboxes) ? exchangeState.mailboxes.length : 'N/A'}</li>
                {exchangeStateModules && (
                  <li>Modules tracked: {Object.keys(exchangeStateModules).join(', ') || 'N/A'}</li>
                )}
              </ul>
              <details className="mt-2">
                <summary className="cursor-pointer text-[11px] font-semibold">View raw state</summary>
                <pre className="bg-blue-100 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900/40 p-2 rounded text-[11px] overflow-auto max-h-64 whitespace-pre-wrap">
                  {JSON.stringify(exchangeState, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      )}

          {(onedriveSummary || onedriveState) && (
            <div className="mt-4 border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50 dark:bg-indigo-900/10 rounded-lg p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-sm">Microsoft 365 OneDrive</h3>
                {onedriveResolvedAt && <span className="text-[11px] text-indigo-700 dark:text-indigo-200">Resolved at {onedriveResolvedAt}</span>}
              </div>
              {onedriveSummary && (
                <ul className="mt-2 space-y-1">
                  <li>Mode: <span className="font-medium">{displayValue(onedriveSummary.mode)}</span></li>
                  <li>Users: <span className="font-medium">{onedriveUsersCount !== null ? onedriveUsersCount.toLocaleString() : 'N/A'}</span></li>
                  {onedriveSampleUsers && <li>Sample: <span className="font-medium">{onedriveSampleUsers}</span></li>}
                  {onedriveSummary.primary_user && <li>Primary user: <span className="font-medium">{displayValue(onedriveSummary.primary_user)}</span></li>}
                  {onedriveSummary.admin_user && <li>Admin user: <span className="font-medium">{displayValue(onedriveSummary.admin_user)}</span></li>}
                  {onedriveSummary.group_email && <li>Group: <span className="font-medium">{displayValue(onedriveSummary.group_email)}</span></li>}
                  {onedriveIncludeShared !== undefined && (
                    <li>Shared included: <span className="font-medium">{onedriveIncludeShared ? 'Yes' : 'No'}</span></li>
                  )}
                  {onedriveFilesCount !== null && (
                    <li>Files exported: <span className="font-medium">{onedriveFilesCount.toLocaleString()}</span></li>
                  )}
                  {onedriveBytesCount !== null && (
                    <li>Bytes exported: <span className="font-medium">{onedriveBytesCount.toLocaleString()}</span></li>
                  )}
                  {onedriveQuery && <li>Query: <span className="font-medium">{onedriveQuery}</span></li>}
                  {onedriveExcludeExtensions.length > 0 && (
                    <li>Excluded extensions: <span className="font-medium">{onedriveExcludeExtensions.join(', ')}</span></li>
                  )}
                  {onedriveMaxFiles !== undefined && (
                    <li>File limit: <span className="font-medium">{onedriveMaxFiles.toLocaleString()}</span></li>
                  )}
                  {onedriveMaxBytes !== undefined && (
                    <li>Byte limit: <span className="font-medium">{onedriveMaxBytes.toLocaleString()}</span></li>
                  )}
                </ul>
              )}
              {onedriveState && (
                <div className="mt-3">
                  <h4 className="font-semibold text-[11px] uppercase tracking-wide text-indigo-800 dark:text-indigo-200">State</h4>
                  <ul className="mt-1 space-y-1">
                    <li>Updated at: {onedriveStateUpdated || displayValue(onedriveState.updated_at)}</li>
                    {onedriveTrackedUsers !== null && <li>Tracked users: {onedriveTrackedUsers.toLocaleString()}</li>}
                    {onedriveTrackedDrives !== null && <li>Tracked drives: {onedriveTrackedDrives.toLocaleString()}</li>}
                  </ul>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-[11px] font-semibold">View raw state</summary>
                    <pre className="bg-indigo-100 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-900/40 p-2 rounded text-[11px] overflow-auto max-h-64 whitespace-pre-wrap">
                      {JSON.stringify(onedriveState, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          )}

      {job.chain_id && (
        <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900/40">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Backup chain #{job.chain_id}</h3>
            <button className="btn-secondary text-xs" onClick={handleResetChain} disabled={isResetting}>
              {isResetting ? 'Resetting…' : 'Reset chain'}
            </button>
          </div>
          {chainLoading ? (
            <p className="text-xs text-gray-500 mt-2">Loading chain…</p>
          ) : chainError ? (
            <p className="text-xs text-red-500 mt-2">{chainError}</p>
          ) : chain ? (
            <ul className="mt-2 text-xs space-y-1">
              <li>Status: <span className="font-medium uppercase">{chain.status}</span></li>
              <li>Repository: {chain.repository_name || chain.repository_id}</li>
              <li>Target: {chain.target_name || chain.target_id}</li>
              <li>
                Last full job:{' '}
                {chain.last_full_job_id ? (
                  <Link className="link" to={`/jobs/${chain.last_full_job_id}`}>
                    #{chain.last_full_job_id}
                  </Link>
                ) : '—'}
              </li>
              <li>
                Last snapshot job:{' '}
                {chain.last_snapshot_job_id ? (
                  <Link className="link" to={`/jobs/${chain.last_snapshot_job_id}`}>
                    #{chain.last_snapshot_job_id}
                  </Link>
                ) : '—'}
              </li>
              <li>Last snapshot id: <code>{chain.last_snapshot_id || '—'}</code></li>
              <li>Mode: {chain.last_snapshot_mode || '—'}</li>
              <li>Non-full count: {chain.non_full_count}</li>
              <li>Updated at: {chain.updated_at ? new Date(chain.updated_at).toLocaleString() : '—'}</li>
            </ul>
          ) : (
            <p className="text-xs text-gray-500 mt-2">Chain data not available.</p>
          )}
        </div>
      )}

      <details className="mt-4">
        <summary className="cursor-pointer text-sm font-semibold">Payload</summary>
        <pre className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-2 rounded text-xs overflow-auto max-h-80 whitespace-pre-wrap">
          {JSON.stringify(job.payload ?? {}, null, 2)}
        </pre>
      </details>
      <details className="mt-2">
        <summary className="cursor-pointer text-sm font-semibold">Result</summary>
        <pre className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-2 rounded text-xs overflow-auto max-h-80 whitespace-pre-wrap">
          {JSON.stringify(job.result ?? {}, null, 2)}
        </pre>
      </details>

      <div className="my-2">
        <a className="btn-secondary" href={`/jobs/${job.id}/log`} target="_blank" rel="noreferrer">
          Download log
        </a>
      </div>
      <h3 className="font-semibold mt-3">Log</h3>
      <pre className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-2 rounded text-xs overflow-auto max-h-96 whitespace-pre-wrap">
        {job.log || '(no log)'}
      </pre>
    </div>
  )
}
